import {ApolloLink} from '@apollo/client';
import createSplitLink from 'src/public/links/createSplitLink';

import type {GraphQlConfig, InternalOperation} from 'src/types';

const V1_GRAPHQL_TAG = 'gql';
const V2_GRAPHQL_TAG = 'graphql';

export type OverrideOperationConfig = {
  /**
   * Fraction of eligible requests routed to V2, where 1 overrides 100%.
   */
  rate: number;
};

/**
 * Rates are evaluated per request, so a fractional rate can route the same
 * operation to V1 on one request and V2 on the next, with both responses
 * landing in the same Apollo cache. Prefer 0 or 1 per operation — treat
 * `rate` as a kill-switch rather than a sampler — unless V1/V2 shape
 * parity for that operation has been proven by the shadow pipeline's
 * `verify_schema` stage.
 */
export type OverrideLinkConfig = {
  /**
   * Omitted means off (fail closed).
   */
  enableOverrides?: boolean;
  getOperationConfig?: (operationName: string) => OverrideOperationConfig;
};

const NO_OVERRIDE_CONFIG: OverrideOperationConfig = {rate: 0};
const neverOverride = (): OverrideOperationConfig => NO_OVERRIDE_CONFIG;

const shouldOverride = (config: OverrideLinkConfig) => {
  const getOperationConfig = config.getOperationConfig ?? neverOverride;

  return (operation: InternalOperation): boolean => {
    if (operation.getContext().queryTag !== V1_GRAPHQL_TAG) {
      return false;
    }

    const {rate} = getOperationConfig(operation.operationName);
    // Skip the RNG call for a non-positive rate. Written as !(rate > 0) rather
    // than rate <= 0 so that a NaN rate also short-circuits here instead of
    // falling through to a comparison that is always false.
    if (!(rate > 0)) {
      return false;
    }

    return Math.random() < rate;
  };
};

// TODO(alusco): This doesn't really work since it will use the v2 version
export default function createOverriddenV1ToV2Link(
  graphqlConfig: GraphQlConfig,
): ApolloLink {
  const v2Link = graphqlConfig.schemas[V2_GRAPHQL_TAG]?.apolloLink;
  const overrideLinkConfig = graphqlConfig.unstable_overrideLinkConfig ?? {};

  // Gate once at construction from the static rootFlags read rather than
  // inside shouldOverride's per-request hot path, since flags can't flip
  // mid-session.
  return v2Link && overrideLinkConfig.enableOverrides
    ? ApolloLink.split(
        shouldOverride(overrideLinkConfig),
        v2Link,
        createSplitLink(graphqlConfig),
      )
    : createSplitLink(graphqlConfig);
}
